import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";

const THUMB_WIDTH = 8;
const TRACK_WIDTH = 8;

interface CustomScrollbarsProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  sx?: object;
  trackOffsetTop?: number;
}

const CustomScrollbars: React.FC<CustomScrollbarsProps> = ({
  children,
  style,
  className,
  sx,
  trackOffsetTop = 0,
}) => {
  const viewRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(20);
  const [visible, setVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  const updateThumb = useCallback(() => {
    const el = viewRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const show = scrollHeight > clientHeight;
    setVisible(show);
    if (!show) return;
    const trackHeight = el.clientHeight - trackOffsetTop;
    const thumbH = Math.max(20, (clientHeight / scrollHeight) * trackHeight);
    const maxScroll = scrollHeight - clientHeight;
    const thumbTopPx =
      maxScroll <= 0 ? 0 : (scrollTop / maxScroll) * (trackHeight - thumbH);
    setThumbHeight(thumbH);
    setThumbTop(thumbTopPx);
  }, [trackOffsetTop]);

  useEffect(() => {
    const el = viewRef.current;
    if (!el) return;
    updateThumb();
    const ro = new ResizeObserver(updateThumb);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateThumb, children]);

  useEffect(() => {
    const t = setTimeout(updateThumb, 0);
    return () => clearTimeout(t);
  }, [updateThumb, children]);

  const handleScroll = useCallback(() => {
    if (dragging) return;
    updateThumb();
  }, [dragging, updateThumb]);

  const handleThumbMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!viewRef.current) return;
      setDragging(true);
      dragStartY.current = e.clientY;
      dragStartScrollTop.current = viewRef.current.scrollTop;
    },
    [],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const el = viewRef.current;
      if (!el) return;
      const dy = e.clientY - dragStartY.current;
      const trackHeight = el.clientHeight - trackOffsetTop;
      const { scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;
      const thumbH = Math.max(20, (clientHeight / scrollHeight) * trackHeight);
      const trackThumbSpace = trackHeight - thumbH;
      const scrollPerPx = maxScroll / trackThumbSpace;
      const newScroll = Math.max(
        0,
        Math.min(maxScroll, dragStartScrollTop.current + dy * scrollPerPx),
      );
      el.scrollTop = newScroll;
      updateThumb();
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, trackOffsetTop, updateThumb]);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = viewRef.current;
      const track = e.currentTarget;
      if (!el || !track || dragging) return;
      const rect = track.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const trackHeight = rect.height;
      const { scrollHeight, clientHeight } = el;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;
      const thumbH = Math.max(20, (clientHeight / scrollHeight) * trackHeight);
      const trackThumbSpace = trackHeight - thumbH;
      const ratio = Math.max(0, Math.min(1, (y - thumbH / 2) / trackThumbSpace));
      el.scrollTop = ratio * maxScroll;
    },
    [dragging],
  );

  return (
    <Box
      className={className}
      sx={{
        position: "relative",
        display: "flex",
        flex: 1,
        minHeight: 0,
        ...sx,
      }}
      style={style}
    >
      <Box
        ref={viewRef}
        onScroll={handleScroll}
        className="custom-scrollbars-hide-native"
        sx={{
          flex: 1,
          overflow: "auto",
          minHeight: 0,
          overscrollBehavior: "contain",
        }}
      >
        {children}
      </Box>
      {visible && (
        <Box
          onClick={handleTrackClick}
          sx={{
            position: "absolute",
            top: trackOffsetTop,
            right: 0,
            width: TRACK_WIDTH,
            height: trackOffsetTop > 0 ? `calc(100% - ${trackOffsetTop}px)` : "100%",
            borderRadius: 1,
            backgroundColor: "transparent",
            cursor: "pointer",
          }}
        >
          <Box
            ref={thumbRef}
            onMouseDown={handleThumbMouseDown}
            sx={{
              position: "absolute",
              left: 2,
              width: 4,
              top: thumbTop,
              height: thumbHeight,
              borderRadius: 1,
              backgroundColor: "rgba(0, 0, 0, 0.25)",
              cursor: "grab",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.4)" },
              "&:active": { cursor: "grabbing" },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default CustomScrollbars;
